import { Component } from "@angular/core";
import { ColDef, GridReadyEvent } from "@ag-grid-community/core";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { AgFns, GridOptionsExt } from "./ag-fns";
import { DialogBaseComponent, DialogParams } from "./dialog-base.component";
import { DomainService } from "./domain.service";

/**
 * Parameters for one "Analyze" result. The caller loads its own rows and describes its own
 * columns. The dialog owns everything else — the tool panels, the pivot and row group panels,
 * the Excel export and the sizing.
 */
export interface AnalysisGridDialogParams extends DialogParams {
  /** Dialog header, and the Excel sheet name. */
  title: string;
  /** The rows to analyze. */
  rowData: any[];
  columnDefs: ColDef[];
  /** Excel file name, without the date stamp or extension. Defaults to 'analysis'. */
  exportFileName?: string;
  /** Short sentence under the header stating what the rows are. Optional. */
  subtitle?: string;
  /** groupDefaultExpanded. -1 expands every group. Defaults to 0 (collapsed). */
  groupDefaultExpanded?: number;
}

/**
 * Reusable analysis dialog for "Analyze" and "Export" actions.
 *
 * Ported from Proximity's AnalysisGridDialogComponent so both systems analyse the same way: one
 * component, every caller supplying only a title, rows and column definitions. What it adds over
 * an ordinary grid is the AG Grid Enterprise tool set — column chooser, filter panel, pivot mode,
 * row grouping and Excel export — which is the point of an Analyze screen.
 *
 * RULE: feed this dialog plain rows. NEVER Breeze entities.
 * Breeze entities are proxied, observable objects. Pivot and row grouping re-key and aggregate
 * rows as plain data and do not behave well over those proxies. Pass a DTO, or map to a plain
 * object first. This is settled; do not weigh a Breeze entity against a DTO here.
 *
 * SharedModule must register SideBarModule, ColumnsToolPanelModule, FiltersToolPanelModule and
 * ExcelExportModule, or the side bar silently fails to appear.
 */
@Component({
  templateUrl: "./analysis-grid-dialog.component.html",
  standalone: false,
})
export class AnalysisGridDialogComponent extends DialogBaseComponent<AnalysisGridDialogParams> {
  gridOptions: GridOptionsExt;

  constructor(
    public config: DynamicDialogConfig<AnalysisGridDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      header: this.params?.title ?? "Analyze",
      width: this.params?.dialogWidth ?? "85vw",
      height: this.params?.dialogHeight ?? "75vh",
      contentStyle: { overflow: "hidden" },
    });

    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      cellSelection: true,
      rowGroupPanelShow: "always",
      pivotPanelShow: "always",
      groupDefaultExpanded: this.params?.groupDefaultExpanded ?? 0,
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        enableRowGroup: true,
        enablePivot: true,
        enableValue: true,
      },
      sideBar: {
        toolPanels: [
          {
            id: "columns",
            labelDefault: "Columns",
            labelKey: "columns",
            iconKey: "columns",
            toolPanel: "agColumnsToolPanel",
            toolPanelParams: {
              suppressRowGroups: false,
              suppressValues: false,
              suppressPivots: false,
              suppressPivotMode: false,
            },
          },
          {
            id: "filters",
            labelDefault: "Filters",
            labelKey: "filters",
            iconKey: "filter",
            toolPanel: "agFiltersToolPanel",
          },
        ],
        // Closed on open. The panels are there when wanted without stealing width by default.
        defaultToolPanel: "",
      },
    });

    this.isPageReady = true;
  }

  onGridReady = (event: GridReadyEvent) => {
    // Applies the project's column formatting — right-aligns Amt/Pct/Count fields, adds formatters.
    AgFns.initGrid(event, this.params.columnDefs, []);
    this.gridOptions.apiX?.setGridOption("rowData", this.params.rowData);
    this.gridOptions.apiX?.sizeColumnsToFit();
  };

  onExport() {
    const name = this.params.exportFileName || "analysis";
    this.gridOptions.apiX?.exportDataAsExcel({
      fileName: `${name}_${new Date().toISOString().split("T")[0]}.xlsx`,
      // 31 characters is Excel's hard limit on a sheet name.
      sheetName: (this.params.title ?? "Analysis").substring(0, 31),
    });
  }

  onClose() {
    this.dialogRef.close(null);
  }
}
